import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, Eye, EyeOff, LogOut } from "lucide-react";

/**
 * Blocking screen shown when the current user's `must_change_password` flag
 * is set (e.g. freshly auto-provisioned Factory Admin logins).
 *
 * There is no way to navigate the app until the password is changed
 * successfully. A "Sign out" escape hatch is provided in case the user
 * is on the wrong account.
 */
export default function ForcePasswordChange() {
  const { setUser, logout, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const rules = [
    { ok: next.length >= 8, text: "At least 8 characters" },
    { ok: /[A-Z]/.test(next), text: "One uppercase letter" },
    { ok: /[0-9]/.test(next), text: "One number" },
    { ok: next && next === confirm, text: "New password + confirmation match" },
    { ok: next && next !== current, text: "Different from current password" },
  ];
  const allOk = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) {
      toast.error("Please satisfy all password rules");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated — welcome!");
      // Refresh /auth/me so must_change_password flips to false in context
      try {
        const me = await api.get("/auth/me");
        localStorage.setItem("bmf_user", JSON.stringify(me.data));
        setUser(me.data);
      } catch (_) {
        // Fallback: update the local user object directly
        const merged = { ...(user || {}), must_change_password: false };
        localStorage.setItem("bmf_user", JSON.stringify(merged));
        setUser(merged);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1a0e08]/95 backdrop-blur-sm p-4"
      data-testid="force-pw-change-screen"
    >
      <div className="w-full max-w-md bg-white border border-[#EAE5D9] rounded-lg shadow-2xl overflow-hidden">
        <div className="p-6 bg-gradient-to-br from-[#FEF9EA] to-white border-b border-[#EAE5D9]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C05621] flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="uc-label !text-[#C05621]">Security · First login</div>
              <h1 className="font-[Outfit] text-2xl font-semibold">Change your password</h1>
            </div>
          </div>
          <p className="text-sm text-[#5C4033] mt-3">
            Hi <b>{user?.name}</b>, this is a temporary password. Please set a new one to continue using Bakemill.
          </p>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <Label>Current (temporary) password</Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoFocus
                data-testid="force-pw-current"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-2 text-[#8B7355]"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label>New password</Label>
            <Input
              type={showPw ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="force-pw-new"
              required
            />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="force-pw-confirm"
              required
            />
          </div>

          <ul className="text-xs space-y-1 bg-[#FDFAF3] border border-[#EAE5D9] rounded p-3">
            {rules.map((r, i) => (
              <li key={i} className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-700" : "text-[#8B7355]"}`}>
                <ShieldCheck className={`w-3.5 h-3.5 ${r.ok ? "opacity-100" : "opacity-30"}`} />
                {r.text}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={logout}
              className="text-[#8B7355]"
              data-testid="force-pw-logout"
            >
              <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
            </Button>
            <Button
              type="submit"
              disabled={busy || !allOk}
              className="flex-1 bg-[#C05621] text-white hover:bg-[#a04519]"
              data-testid="force-pw-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
